import type {
  PolicyReportContextBlock,
  ClaimsRoiAlignmentContextBlock,
} from '../policies/policyPacks';
import { buildClaimsRoiContextForAlias, buildReportContextForAlias } from '../policies/policyPacks';
import type {
  ClaimsRoiAlignmentPayload,
  PolicyReportContextPayload,
  SafetyCaseRecord,
  SafetyDashboardRow,
  SafetyReportArtifactDetailResponse,
  ReliabilityProofPackPreviewResponse,
} from '../../services/reportsApi';

export type ClaimsRoiAlignmentViewModel = {
  claimsAlignmentCategory: string;
  claimsCostDriverFamily: string;
  commercialPriority: string;
  commercialPriorityBasis: string[];
  claimCharacteristicHints: string[];
  targetIndustryHints: string[];
  industrySectorHints: string[];
  classificationUnitHints: string[];
  recommendedFirstScan: string;
  recommendedPilotTemplates: string[];
  buyerPainLanguage: string;
  salesDiscoveryQuestions: string[];
  benefitTypeRelevance: string[];
  roiRationale: string;
  uxSummary: string;
  reportSummary: string;
  claimBoundary: string;
  sourceLabel: string;
};

function asObject(value: unknown): Record<string, unknown> {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};
}

function asText(value: unknown): string {
  return String(value || '').trim();
}

function asList(value: unknown): string[] {
  return Array.isArray(value)
    ? value.map((item) => String(item || '').trim()).filter(Boolean)
    : [];
}

function normalizeAlignment(value: unknown): ClaimsRoiAlignmentContextBlock | null {
  const raw = asObject(value);
  const hasContent = Object.keys(raw).some((key) => {
    const current = raw[key];
    return (Array.isArray(current) && current.length > 0) || asText(current).length > 0;
  });
  return hasContent ? (raw as ClaimsRoiAlignmentContextBlock) : null;
}

function toViewModelFromAlignment(
  alignment: ClaimsRoiAlignmentContextBlock | ClaimsRoiAlignmentPayload | unknown,
): ClaimsRoiAlignmentViewModel | null {
  const normalized = normalizeAlignment(alignment);
  if (!normalized) return null;
  return {
    claimsAlignmentCategory: asText(normalized.claims_alignment_category),
    claimsCostDriverFamily: asText(normalized.claims_cost_driver_family),
    commercialPriority: asText(normalized.commercial_priority),
    commercialPriorityBasis: asList(normalized.commercial_priority_basis),
    claimCharacteristicHints: asList(normalized.claim_characteristic_hints),
    targetIndustryHints: asList(normalized.target_industry_hints),
    industrySectorHints: asList(normalized.industry_sector_hints),
    classificationUnitHints: asList(normalized.classification_unit_hints),
    recommendedFirstScan: asText(normalized.recommended_first_scan),
    recommendedPilotTemplates: asList(normalized.recommended_pilot_templates),
    buyerPainLanguage: asText(normalized.buyer_pain_language),
    salesDiscoveryQuestions: asList(normalized.sales_discovery_questions),
    benefitTypeRelevance: asList(normalized.benefit_type_relevance),
    roiRationale: asText(normalized.roi_rationale),
    uxSummary: asText(normalized.ux_summary),
    reportSummary: asText(normalized.report_summary),
    claimBoundary: asText(normalized.claim_boundary),
    sourceLabel: asText(normalized.source_label) || 'WorkSafeBC claims / cost-driver alignment',
  };
}

function toViewModel(
  policyContext: PolicyReportContextPayload | PolicyReportContextBlock | null | undefined,
): ClaimsRoiAlignmentViewModel | null {
  const context = policyContext || null;
  if (!context) return null;
  return toViewModelFromAlignment(context.claims_roi_alignment);
}

function inferAliasFromRecord(record: {
  riskType?: unknown;
  activityType?: unknown;
  riskLabel?: unknown;
  hazardCategory?: unknown;
  unsafeConditionTitle?: unknown;
}): string | null {
  const riskType = asText(record.riskType).toLowerCase();
  const text = [
    record.activityType,
    record.riskLabel,
    record.hazardCategory,
    record.unsafeConditionTitle,
  ]
    .map(asText)
    .join(' ')
    .toLowerCase();
  if (text.includes('material load')) return 'material_load_interaction';
  if (riskType === 'ppe_compliance' || text.includes('ppe')) return 'ppe_compliance';
  if (riskType === 'work_at_height' || text.includes('work at height') || text.includes('fall')) {
    return 'work_at_height';
  }
  if (text.includes('ergonomic') || text.includes('material handling') || text.includes('msi')) {
    return 'ergonomics_material_handling';
  }
  if (riskType === 'zone_intrusion' && (text.includes('forklift') || text.includes('mobile equipment'))) {
    return 'pedestrian_in_forklift_zone';
  }
  if (riskType === 'zone_intrusion') return 'person_in_restricted_area';
  return buildReportContextForAlias(riskType) ? riskType : null;
}

export function resolveClaimsRoiFromPolicyConfig(
  config: Record<string, unknown> | null | undefined,
): ClaimsRoiAlignmentViewModel | null {
  const raw = asObject(config);
  const directContext = asObject(raw.report_context) as PolicyReportContextPayload;
  if (directContext?.claims_roi_alignment) return toViewModel(directContext);
  const alias = asText(asObject(raw.ui_context).policy_alias);
  return toViewModelFromAlignment(alias ? buildClaimsRoiContextForAlias(alias) : null);
}

export function resolveClaimsRoiFromAlias(
  alias: string | null | undefined,
): ClaimsRoiAlignmentViewModel | null {
  const key = asText(alias);
  return toViewModelFromAlignment(key ? buildClaimsRoiContextForAlias(key) : null);
}

export function resolveClaimsRoiFromPolicyContext(
  policyContext: PolicyReportContextPayload | PolicyReportContextBlock | null | undefined,
): ClaimsRoiAlignmentViewModel | null {
  return toViewModel(policyContext);
}

export function resolveClaimsRoiFromArtifactDetail(
  detail: SafetyReportArtifactDetailResponse | null | undefined,
): ClaimsRoiAlignmentViewModel | null {
  const candidates = [
    detail?.technical_details?.policy_report_context,
    detail?.technical_details?.report_context,
    detail?.safety_scan_buyer_report?.policy_report_context,
  ];
  for (const candidate of candidates) {
    const resolved = toViewModel(candidate || null);
    if (resolved) return resolved;
  }
  const alias = inferAliasFromRecord({
    riskType: detail?.artifact?.scope?.risk_type,
    activityType: detail?.artifact?.display_labels?.activity_type_label,
    riskLabel: detail?.artifact?.display_labels?.risk_level,
    unsafeConditionTitle: detail?.artifact?.summary,
  });
  return toViewModel(alias ? buildReportContextForAlias(alias) : null);
}

export function resolveClaimsRoiFromProofPreview(
  preview: ReliabilityProofPackPreviewResponse | null | undefined,
): ClaimsRoiAlignmentViewModel | null {
  const context = asObject(
    preview?.evidence_pack?.report_projection?.label_bundle?.policy_report_context,
  ) as PolicyReportContextPayload;
  return toViewModel(context);
}

export function resolveClaimsRoiFromSafetyCase(
  item: SafetyCaseRecord | SafetyDashboardRow | null | undefined,
): ClaimsRoiAlignmentViewModel | null {
  const scope = asObject((item as SafetyCaseRecord | undefined)?.scope);
  const topLevel = asObject(item);
  const directContext = asObject(
    topLevel.policy_report_context
    || topLevel.report_context
    || scope.policy_report_context
    || scope.report_context,
  ) as PolicyReportContextPayload;
  if (directContext?.claims_roi_alignment) return toViewModel(directContext);
  const alias = inferAliasFromRecord({
    riskType: item?.risk_type,
    activityType: (item as SafetyDashboardRow | undefined)?.activity_type,
    riskLabel: item?.risk_label,
    hazardCategory: item?.hazard_category,
    unsafeConditionTitle: (item as SafetyDashboardRow | undefined)?.unsafe_condition_title,
  });
  return toViewModelFromAlignment(alias ? buildClaimsRoiContextForAlias(alias) : null);
}
